import { computed, inject } from '@angular/core';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import {
  catchError,
  concatMap,
  filter,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  pipe,
  switchMap,
  tap,
  toArray,
} from 'rxjs';
import type DataSource from 'devextreme/data/data_source';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import { APP_CONFIG } from '@application/config';
import { DxOdataStoreService } from '@shared/components/dx-data-grid/data-access';
import {
  AcademyLearningState,
  initialAcademyLearningState,
} from './entities/academy-learning-store.model';
import {
  CourseEntity,
  CourseItemEntity,
} from './entities/academy-entities.model';
import {
  CourseCreateRequest,
  CourseUpdateRequest,
  CourseResponse,
  LearningPathCourseResponse,
  LearningPathCreateRequest,
  LearningPathResponse,
} from './entities/academy.model';
import { ProductType } from './entities/academy-enums.model';
import {
  DiscoverQuery,
  MineCoursesQuery,
  MinePathsQuery,
} from './entities/academy-query.model';
import { CoursesService } from './infra/courses.service';
import { LearningPathsService } from './infra/learning-paths.service';

/**
 * `CourseId` is a `Guid` on the wire. Guard against route-segment leaks (e.g. a
 * navigation that drops the id and lets the static `content`/`edit` segment land in
 * the `:courseId` slot), which would otherwise reach OData as `CourseId eq content`
 * — an unquoted bareword the backend rejects with 400.
 */
const GUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ALL_OPS = [
  'discoverCourses',
  'loadCourses',
  'loadCourse',
  'loadAllCourseItems',
  'loadCourseItems',
  'reorderCourseItems',
  'removeCourseItem',
  'updateCourseItem',
  'updateCourse',
  'removeCourse',
  'setCourseItemRequired',
  'uploadItemVideo',
  'assembleCourse',
  'appendCourseItems',
  'setCover',
  'loadAttachments',
  'addAttachment',
  'removeAttachment',
  'discoverPaths',
  'loadProducerPaths',
  'loadPath',
  'loadPathCourses',
  'assemblePath',
  'appendPathCourses',
  'setPathCover',
  'requestItemPlaybackToken',
  'loadCourseProgress',
  'loadPathProgress',
  'createCourse',
  'createPath',
  'addCourseItem',
  'publishCourse',
  'reportCourseItemProgress',
  'addPathCourse',
  'removePathCourse',
  'removePath',
  'reorderPathCourses',
  'publishPath',
  'loadMyCourses',
  'loadMyPaths',
] as const;

function index<T>(
  byId: Record<string, T>,
  items: T[],
  key: (i: T) => string
): Record<string, T> {
  const next = { ...byId };
  for (const item of items) {
    next[key(item)] = { ...next[key(item)], ...item };
  }
  return next;
}

export const AcademyLearningStore = signalStore(
  { providedIn: 'root' },
  withState<AcademyLearningState>(initialAcademyLearningState),
  withReqState(...ALL_OPS),
  withComputed((store) => ({
    courses: computed<CourseResponse[]>(() => {
      const byId = store.coursesById();
      return store
        .courseIds()
        .map((id) => byId[id])
        .filter((c): c is CourseResponse => !!c);
    }),
    paths: computed<LearningPathResponse[]>(() => {
      const byId = store.pathsById();
      return store
        .pathIds()
        .map((id) => byId[id])
        .filter((p): p is LearningPathResponse => !!p);
    }),
    /** Producer-side course list (OData, drafts included) — selector source. */
    producerCourses: computed<CourseResponse[]>(() => {
      const byId = store.coursesById();
      return store
        .producerCourseIds()
        .map((id) => byId[id])
        .filter((c): c is CourseResponse => !!c);
    }),
    /** Producer-side path list (OData, drafts included) — listing source. */
    producerPaths: computed<LearningPathResponse[]>(() => {
      const byId = store.pathsById();
      return store
        .producerPathIds()
        .map((id) => byId[id])
        .filter((p): p is LearningPathResponse => !!p);
    }),
  })),
  withMethods(
    (
      store,
      courses = inject(CoursesService),
      paths = inject(LearningPathsService),
      errorStore = inject(ErrorStore),
      dxOdata = inject(DxOdataStoreService),
      odataUrl = inject(APP_CONFIG).apiUrl.odata_academy_service
    ) => {
      /**
       * Server-side `Courses` DataSource for the producer "Cursos" grid. Hits the full
       * OData controller (`GET /odata/Courses`) so paging/`$filter`/`$orderby` run on the
       * server (mirrors `UserStore.getUsers`). Enum fields (`ProductType`, `CourseStatusType`)
       * arrive as NUMERIC wire codes — `ODataStore` does NOT run `fromWireEnums`, so the grid
       * cell templates render numeric → label/badge themselves.
       */
      const getCoursesDataSource = (): DataSource<CourseEntity> =>
        dxOdata.fromOdata<CourseEntity>({
          url: odataUrl,
          metadata: 'Courses',
          key: 'CourseId',
          params: { $orderby: 'Title' },
        });

      /**
       * Server-side `LearningPaths` DataSource for the producer "Trilhas" grid. Same
       * full-OData wiring as {@link getCoursesDataSource}; enum fields are numeric on the wire.
       */
      const getPathsDataSource = (): DataSource<LearningPathResponse> =>
        dxOdata.fromOdata<LearningPathResponse>({
          url: odataUrl,
          metadata: 'LearningPaths',
          key: 'LearningPathId',
          params: { $orderby: 'Title' },
        });

      const discoverCourses = rxMethod<DiscoverQuery | void>(
        pipe(
          tap(() => patchState(store, setPropLoading('discoverCourses'))),
          switchMap((query) =>
            courses.discover(query || {}).pipe(
              tapResponse({
                next: (items) =>
                  patchState(
                    store,
                    {
                      coursesById: index(
                        store.coursesById(),
                        items,
                        (c) => c.CourseId
                      ),
                      courseIds: items.map((c) => c.CourseId),
                    },
                    setPropLoaded('discoverCourses')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('discoverCourses', error));
                },
              })
            )
          )
        )
      );

      // Producer-side list over the raw OData set: `discover` only returns PUBLISHED
      // courses visible to the viewer's audience; the producer screens need drafts too.
      // Items are loaded separately via `loadAllCourseItems` (GET /OData/CourseItems).
      const loadCourses = rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadCourses'))),
          switchMap(() =>
            courses.list({ orderby: 'Title' }).pipe(
              tapResponse({
                next: (page) =>
                  patchState(
                    store,
                    {
                      coursesById: index(
                        store.coursesById(),
                        page.value,
                        (c) => c.CourseId
                      ),
                      producerCourseIds: page.value.map((c) => c.CourseId),
                    },
                    setPropLoaded('loadCourses')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadCourses', error));
                },
              })
            )
          )
        )
      );

      // Single-entity read (`GET /Courses?id=`): unlike `discover`/`list`, it carries `CoverUrl`.
      // The edit builder calls this so the persisted cover hydrates even on a deep-link/refresh.
      const loadCourse = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadCourse'))),
          switchMap((courseId) =>
            courses.getById(courseId).pipe(
              tapResponse({
                next: (course) =>
                  patchState(
                    store,
                    course
                      ? {
                          coursesById: index(
                            store.coursesById(),
                            [course],
                            (c) => c.CourseId
                          ),
                        }
                      : {},
                    setPropLoaded('loadCourse')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadCourse', error));
                },
              })
            )
          )
        )
      );

      /** `GET /OData/CourseItems?$orderby=Order` — all items; grouped client-side by CourseId. */
      const loadAllCourseItems = rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadAllCourseItems'))),
          switchMap(() =>
            courses.listAllItems().pipe(
              tapResponse({
                next: (page) => {
                  const grouped: Record<string, CourseItemEntity[]> = {};
                  for (const item of page.value) {
                    (grouped[item.CourseId] ??= []).push(item);
                  }
                  patchState(
                    store,
                    { itemsByCourse: { ...store.itemsByCourse(), ...grouped } },
                    setPropLoaded('loadAllCourseItems')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadAllCourseItems', error));
                },
              })
            )
          )
        )
      );

      /** `GET /OData/CourseItems?$filter=CourseId eq {id}&$orderby=Order` — items come sorted. */
      const loadCourseItems = rxMethod<string>(
        pipe(
          filter((courseId) => GUID_RE.test(courseId)),
          tap(() => patchState(store, setPropLoading('loadCourseItems'))),
          switchMap((courseId) =>
            courses.listItems(courseId).pipe(
              tapResponse({
                next: (page) => {
                  patchState(
                    store,
                    {
                      itemsByCourse: {
                        ...store.itemsByCourse(),
                        [courseId]: page.value,
                      },
                    },
                    setPropLoaded('loadCourseItems')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadCourseItems', error));
                },
              })
            )
          )
        )
      );

      /**
       * Bulk-atomic reorder of a course's items (immediate commit): optimistically reorder
       * `itemsByCourse[courseId]` by the given CourseItemId order, `PATCH /items/reorder`, then
       * re-read the server truth (`Order = index`). On error, re-read reverts.
       */
      const reorderCourseItems = rxMethod<{
        courseId: string;
        orderedItemIds: string[];
      }>(
        pipe(
          tap(({ courseId, orderedItemIds }) => {
            const current = store.itemsByCourse()[courseId] ?? [];
            const byId = new Map(current.map((i) => [i.CourseItemId, i]));
            const reordered = orderedItemIds
              .map((id) => byId.get(id))
              .filter((i): i is CourseItemEntity => !!i);
            patchState(
              store,
              {
                itemsByCourse: {
                  ...store.itemsByCourse(),
                  [courseId]: reordered,
                },
              },
              setPropLoading('reorderCourseItems')
            );
          }),
          switchMap(({ courseId, orderedItemIds }) =>
            courses.reorderItems(courseId, orderedItemIds).pipe(
              tapResponse({
                next: () => {
                  patchState(store, setPropLoaded('reorderCourseItems'));
                  loadCourseItems(courseId);
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('reorderCourseItems', error));
                  loadCourseItems(courseId); // revert to server state
                },
              })
            )
          )
        )
      );

      /** `DELETE /Courses/{courseId}/items/{itemId}` (immediate commit), then re-read. */
      const removeCourseItem = rxMethod<{
        courseId: string;
        courseItemId: string;
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('removeCourseItem'))),
          switchMap(({ courseId, courseItemId }) =>
            courses.removeItem(courseId, courseItemId).pipe(
              tapResponse({
                next: () => {
                  patchState(store, setPropLoaded('removeCourseItem'));
                  loadCourseItems(courseId);
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('removeCourseItem', error));
                },
              })
            )
          )
        )
      );

      /** `PATCH /CourseItems?id={guid}` (immediate commit), then re-read. */
      const updateCourseItem = rxMethod<{
        courseId: string;
        courseItemId: string;
        title: string;
        description: string | null;
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('updateCourseItem'))),
          switchMap(({ courseId, courseItemId, title, description }) =>
            courses
              .updateItem(courseItemId, {
                Title: title,
                Description: description,
              })
              .pipe(
                tapResponse({
                  next: () => {
                    patchState(store, setPropLoaded('updateCourseItem'));
                    loadCourseItems(courseId);
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, setPropError('updateCourseItem', error));
                  },
                })
              )
          )
        )
      );

      /** `PATCH /Courses?id={guid}` (immediate commit), then update local course index. */
      const updateCourse = rxMethod<{
        courseId: string;
        title: string;
        description: string | null;
        productType: ProductType;
        liveAudienceType?: string;
        audienceFilter?: string | null;
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('updateCourse'))),
          switchMap(({ courseId, title, description, productType, liveAudienceType, audienceFilter }) =>
            courses
              .update(courseId, {
                Title: title,
                Description: description,
                ProductType: productType,
                ...(liveAudienceType ? { LiveAudienceType: liveAudienceType as any } : {}),
                ...(audienceFilter !== undefined ? { AudienceFilter: audienceFilter } : {}),
              })
              .pipe(
                tapResponse({
                  next: (updated) => {
                    patchState(store, (state) => ({
                      coursesById: {
                        ...state.coursesById,
                        [courseId]: updated,
                      },
                    }));
                    patchState(store, setPropLoaded('updateCourse'));
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, setPropError('updateCourse', error));
                  },
                })
              )
          )
        )
      );

      /** `DELETE /Courses?id={guid}` (immediate commit), then re-read the producer list. */
      const removeCourse = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('removeCourse'))),
          switchMap((courseId) =>
            courses.remove(courseId).pipe(
              tapResponse({
                next: () => {
                  patchState(store, setPropLoaded('removeCourse'));
                  loadCourses();
                  loadAllCourseItems();
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('removeCourse', error));
                },
              })
            )
          )
        )
      );

      /**
       * `PATCH /items/{itemId}/required` (immediate commit) with an optimistic `IsRequired` flip.
       * `mergeMap` (not switchMap): flipping two items quickly must not cancel the first PATCH.
       * On error the flip is reverted in place.
       */
      const setCourseItemRequired = rxMethod<{
        courseId: string;
        courseItemId: string;
        isRequired: boolean;
      }>(
        pipe(
          tap(({ courseId, courseItemId, isRequired }) =>
            patchState(
              store,
              {
                itemsByCourse: {
                  ...store.itemsByCourse(),
                  [courseId]: (store.itemsByCourse()[courseId] ?? []).map((i) =>
                    i.CourseItemId === courseItemId
                      ? { ...i, IsRequired: isRequired }
                      : i
                  ),
                },
              },
              setPropLoading('setCourseItemRequired')
            )
          ),
          mergeMap(({ courseId, courseItemId, isRequired }) =>
            courses.setItemRequired(courseId, courseItemId, isRequired).pipe(
              tapResponse({
                next: () =>
                  patchState(store, setPropLoaded('setCourseItemRequired')),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(
                    store,
                    {
                      itemsByCourse: {
                        ...store.itemsByCourse(),
                        [courseId]: (store.itemsByCourse()[courseId] ?? []).map(
                          (i) =>
                            i.CourseItemId === courseItemId
                              ? { ...i, IsRequired: !isRequired }
                              : i
                        ),
                      },
                    },
                    setPropError('setCourseItemRequired', error)
                  );
                },
              })
            )
          )
        )
      );

      /**
       * End-to-end VOD upload (`guide-upload-video.md`): handshake (`uploadItem` →
       * one-time URL) then the binary straight to Cloudflare, surfacing progress.
       * ONE rxMethod emission wraps both requests — N calls would self-cancel
       * (switchMap), and the one-time URL must be consumed by the same flow that
       * obtained it (~30 min TTL; on failure the orphaned item stays server-side,
       * a retry simply performs a fresh handshake).
       */
      const uploadCourseItemVideo = rxMethod<{
        courseId: string;
        title: string;
        file: File;
        description?: string | null;
      }>(
        pipe(
          tap(() =>
            patchState(
              store,
              { uploadProgress: 0, lastUpload: null },
              setPropLoading('uploadItemVideo')
            )
          ),
          switchMap(({ courseId, title, file, description }) =>
            courses.uploadItem(courseId, title, 0, true, description).pipe(
              switchMap((handshake) =>
                courses.uploadVideoBinary(handshake.UploadUrl, file).pipe(
                  tap((event) => {
                    if (
                      event.type === HttpEventType.UploadProgress &&
                      event.total
                    ) {
                      patchState(store, {
                        uploadProgress: Math.round(
                          (100 * event.loaded) / event.total
                        ),
                      });
                    }
                  }),
                  filter((event) => event.type === HttpEventType.Response),
                  map(() => handshake)
                )
              ),
              tapResponse({
                next: (handshake) =>
                  patchState(
                    store,
                    { lastUpload: handshake, uploadProgress: 100 },
                    setPropLoaded('uploadItemVideo')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('uploadItemVideo', error));
                },
              })
            )
          )
        )
      );

      // --- shared composition for the assemble/append chains (ONE rxMethod emission;
      // chained rxMethod calls would self-cancel under switchMap). ---

      /**
       * Sequential VOD uploads: each `POST /items/upload` handshake creates the CourseItem
       * with the given order, then the binary streams straight to Cloudflare (`concatMap`
       * so uploads don't saturate bandwidth). Course lessons are upload-only.
       */
      const uploadVideos$ = (
        courseId: string,
        videos: {
          file: File;
          title: string;
          order: number;
          isRequired: boolean;
          description?: string | null;
        }[]
      ) =>
        videos.length
          ? from(videos).pipe(
              concatMap((v) =>
                courses
                  .uploadItem(
                    courseId,
                    v.title,
                    v.order,
                    v.isRequired,
                    v.description
                  )
                  .pipe(
                    switchMap((handshake) =>
                      courses
                        .uploadVideoBinary(handshake.UploadUrl, v.file)
                        .pipe(
                          filter(
                            (event) => event.type === HttpEventType.Response
                          )
                        )
                    )
                  )
              ),
              toArray()
            )
          : of([]);

      /** Parallel multipart support-material uploads (`POST /attachments`). */
      const addAttachments$ = (courseId: string, attachments: File[]) =>
        attachments.length
          ? forkJoin(
              attachments.map((file) => courses.addAttachment(courseId, file))
            )
          : of([]);

      /**
       * Course assembly (`montar-curso`): ONE emission performs the full draft-first
       * chain — `POST /Courses` (draft) → optional `POST /cover` → sequential video-aula
       * uploads (lessons are upload-only) → `POST /attachments` per support material →
       * optional `POST /publish`.
       */
      const assembleCourse = rxMethod<{
        course: CourseCreateRequest;
        cover: File | null;
        videos: {
          file: File;
          title: string;
          order: number;
          isRequired: boolean;
          description?: string | null;
        }[];
        attachments: File[];
        publish: boolean;
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('assembleCourse'))),
          switchMap(({ course, cover, videos, attachments, publish }) =>
            courses.create(course).pipe(
              switchMap((created) =>
                (cover
                  ? courses.setCover(created.CourseId, cover)
                  : of(null)
                ).pipe(
                  switchMap(() => uploadVideos$(created.CourseId, videos)),
                  switchMap(() =>
                    addAttachments$(created.CourseId, attachments)
                  ),
                  switchMap(() =>
                    publish ? courses.publish(created.CourseId) : of(void 0)
                  ),
                  map(() =>
                    publish
                      ? { ...created, CourseStatusType: 'Published' as const }
                      : created
                  )
                )
              ),
              tapResponse({
                next: (created) =>
                  patchState(
                    store,
                    {
                      coursesById: index(
                        store.coursesById(),
                        [created],
                        (c) => c.CourseId
                      ),
                      producerCourseIds: [
                        created.CourseId,
                        ...store
                          .producerCourseIds()
                          .filter((id) => id !== created.CourseId),
                      ],
                    },
                    setPropLoaded('assembleCourse')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('assembleCourse', error));
                },
              })
            )
          )
        )
      );

      /**
       * Edit-mode append (`:courseId/edit`): the API has no item update/delete, so
       * editing is append-only — new video-aulas are uploaded AFTER the existing items
       * (`startOrder` = current item count), new support materials are posted, and a
       * draft can be published. Same one-emission composition rationale as `assembleCourse`.
       */
      const appendCourseItems = rxMethod<{
        courseId: string;
        metadata?: CourseUpdateRequest;
        videos: {
          file: File;
          title: string;
          isRequired: boolean;
          description?: string | null;
        }[];
        attachments: File[];
        startOrder: number;
        publish: boolean;
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('appendCourseItems'))),
          switchMap(({ courseId, metadata, videos, attachments, startOrder, publish }) => {
            const updateMetadata$ = metadata
              ? courses.update(courseId, metadata)
              : of(null);

            return updateMetadata$.pipe(
              switchMap((updatedCourse) =>
                uploadVideos$(
                  courseId,
                  videos.map((v, offset) => ({ ...v, order: startOrder + offset }))
                ).pipe(
                  switchMap(() => addAttachments$(courseId, attachments)),
                  switchMap(() =>
                    publish ? courses.publish(courseId) : of(void 0)
                  ),
                  map(() => updatedCourse)
                )
              ),
              tapResponse({
                next: (updatedCourse) => {
                  const cached = store.coursesById()[courseId];
                  patchState(
                    store,
                    {
                      coursesById: {
                        ...store.coursesById(),
                        [courseId]: {
                          ...(cached ?? {}),
                          ...(updatedCourse ?? {}),
                          ...(publish ? { CourseStatusType: 'Published' as const } : {}),
                        } as CourseResponse,
                      },
                    },
                    setPropLoaded('appendCourseItems')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('appendCourseItems', error));
                },
              })
            );
          })
        )
      );

      /** `POST /Courses/{id}/cover` — replace the course cover (edit-mode, immediate). */
      const setCover = rxMethod<{ courseId: string; file: File }>(
        pipe(
          tap(() => patchState(store, setPropLoading('setCover'))),
          switchMap(({ courseId, file }) =>
            courses.setCover(courseId, file).pipe(
              tapResponse({
                next: () => patchState(store, setPropLoaded('setCover')),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('setCover', error));
                },
              })
            )
          )
        )
      );

      /** `GET /Courses/{id}/attachments` — load support materials into `attachmentsByCourse`. */
      const loadAttachments = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadAttachments'))),
          switchMap((courseId) =>
            courses.listAttachments(courseId).pipe(
              tapResponse({
                next: (list) =>
                  patchState(
                    store,
                    {
                      attachmentsByCourse: {
                        ...store.attachmentsByCourse(),
                        [courseId]: list,
                      },
                    },
                    setPropLoaded('loadAttachments')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadAttachments', error));
                },
              })
            )
          )
        )
      );

      /** `POST /Courses/{id}/attachments` — add a support material (edit-mode, immediate). */
      const addAttachment = rxMethod<{ courseId: string; file: File }>(
        pipe(
          tap(() => patchState(store, setPropLoading('addAttachment'))),
          // mergeMap (not switchMap): dropping N files fires N emissions — switchMap would
          // cancel all but the last, persisting only one attachment.
          mergeMap(({ courseId, file }) =>
            courses.addAttachment(courseId, file).pipe(
              tapResponse({
                next: (created) =>
                  patchState(
                    store,
                    {
                      attachmentsByCourse: {
                        ...store.attachmentsByCourse(),
                        [courseId]: [
                          ...(store.attachmentsByCourse()[courseId] ?? []),
                          created,
                        ],
                      },
                    },
                    setPropLoaded('addAttachment')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('addAttachment', error));
                },
              })
            )
          )
        )
      );

      /** `DELETE /Courses/{courseId}/attachments/{attachmentId}` — remove a support material. */
      const removeAttachment = rxMethod<{
        courseId: string;
        attachmentId: string;
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('removeAttachment'))),
          mergeMap(({ courseId, attachmentId }) =>
            courses.removeAttachment(courseId, attachmentId).pipe(
              tapResponse({
                next: () =>
                  patchState(
                    store,
                    {
                      attachmentsByCourse: {
                        ...store.attachmentsByCourse(),
                        [courseId]: (
                          store.attachmentsByCourse()[courseId] ?? []
                        ).filter((a) => a.CourseAttachmentId !== attachmentId),
                      },
                    },
                    setPropLoaded('removeAttachment')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('removeAttachment', error));
                },
              })
            )
          )
        )
      );

      // Producer-side path list over the raw OData set: `discover` only returns
      // PUBLISHED paths visible to the viewer; the producer Trilhas screen needs
      // drafts too. Mirrors `loadCourses`.
      const loadProducerPaths = rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadProducerPaths'))),
          switchMap(() =>
            paths.list({ orderby: 'Title' }).pipe(
              tapResponse({
                next: (page) =>
                  patchState(
                    store,
                    {
                      pathsById: index(
                        store.pathsById(),
                        page.value,
                        (p) => p.LearningPathId
                      ),
                      producerPathIds: page.value.map((p) => p.LearningPathId),
                    },
                    setPropLoaded('loadProducerPaths')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadProducerPaths', error));
                },
              })
            )
          )
        )
      );

      // Single-entity read (`GET /LearningPaths?id=`): carries `CoverUrl`, which the
      // `discover`/`list` projections omit. Used by the edit path builder to hydrate the cover.
      const loadPath = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadPath'))),
          switchMap((pathId) =>
            paths.getById(pathId).pipe(
              tapResponse({
                next: (path) =>
                  patchState(
                    store,
                    path
                      ? {
                          pathsById: index(
                            store.pathsById(),
                            [path],
                            (p) => p.LearningPathId
                          ),
                        }
                      : {},
                    setPropLoaded('loadPath')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadPath', error));
                },
              })
            )
          )
        )
      );

      const discoverPaths = rxMethod<DiscoverQuery | void>(
        pipe(
          tap(() => patchState(store, setPropLoading('discoverPaths'))),
          switchMap((query) =>
            paths.discover(query || {}).pipe(
              tapResponse({
                next: (items) =>
                  patchState(
                    store,
                    {
                      pathsById: index(
                        store.pathsById(),
                        items,
                        (p) => p.LearningPathId
                      ),
                      pathIds: items.map((p) => p.LearningPathId),
                    },
                    setPropLoaded('discoverPaths')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('discoverPaths', error));
                },
              })
            )
          )
        )
      );

      const requestItemPlaybackToken = rxMethod<{
        courseId: string;
        itemId: string;
      }>(
        pipe(
          tap(() =>
            patchState(store, setPropLoading('requestItemPlaybackToken'))
          ),
          switchMap(({ courseId, itemId }) =>
            courses.itemPlaybackToken(courseId, itemId).pipe(
              tapResponse({
                next: (res) =>
                  patchState(
                    store,
                    {
                      playbackTokenByKey: {
                        ...store.playbackTokenByKey(),
                        [`item:${courseId}:${itemId}`]: res.Token,
                      },
                    },
                    setPropLoaded('requestItemPlaybackToken')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(
                    store,
                    setPropError('requestItemPlaybackToken', error)
                  );
                },
              })
            )
          )
        )
      );

      const loadCourseProgress = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadCourseProgress'))),
          switchMap((courseId) =>
            courses.progress(courseId).pipe(
              tapResponse({
                next: (progress) =>
                  patchState(
                    store,
                    {
                      courseProgressById: {
                        ...store.courseProgressById(),
                        [courseId]: progress,
                      },
                    },
                    setPropLoaded('loadCourseProgress')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadCourseProgress', error));
                },
              })
            )
          )
        )
      );

      const loadPathProgress = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadPathProgress'))),
          switchMap((pathId) =>
            paths.progress(pathId).pipe(
              tapResponse({
                next: (progress) =>
                  patchState(
                    store,
                    {
                      pathProgressById: {
                        ...store.pathProgressById(),
                        [pathId]: progress,
                      },
                    },
                    setPropLoaded('loadPathProgress')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadPathProgress', error));
                },
              })
            )
          )
        )
      );

      const createCourse = rxMethod<CourseCreateRequest>(
        pipe(
          tap(() => patchState(store, setPropLoading('createCourse'))),
          switchMap((vm) =>
            courses.create(vm).pipe(
              tapResponse({
                next: (created) =>
                  patchState(
                    store,
                    {
                      coursesById: index(
                        store.coursesById(),
                        [created],
                        (c) => c.CourseId
                      ),
                      courseIds: [
                        created.CourseId,
                        ...store
                          .courseIds()
                          .filter((id) => id !== created.CourseId),
                      ],
                    },
                    setPropLoaded('createCourse')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('createCourse', error));
                },
              })
            )
          )
        )
      );

      const createPath = rxMethod<LearningPathCreateRequest>(
        pipe(
          tap(() => patchState(store, setPropLoading('createPath'))),
          switchMap((vm) =>
            paths.create(vm).pipe(
              tapResponse({
                next: (created) =>
                  patchState(
                    store,
                    {
                      pathsById: index(
                        store.pathsById(),
                        [created],
                        (p) => p.LearningPathId
                      ),
                      pathIds: [
                        created.LearningPathId,
                        ...store
                          .pathIds()
                          .filter((id) => id !== created.LearningPathId),
                      ],
                    },
                    setPropLoaded('createPath')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('createPath', error));
                },
              })
            )
          )
        )
      );

      const addCourseItem = rxMethod<{
        courseId: string;
        liveEventId: string;
        order: number;
        isRequired?: boolean;
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('addCourseItem'))),
          switchMap(({ courseId, liveEventId, order, isRequired }) =>
            courses.addItem(courseId, liveEventId, order, isRequired).pipe(
              tapResponse({
                next: () => patchState(store, setPropLoaded('addCourseItem')),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('addCourseItem', error));
                },
              })
            )
          )
        )
      );

      const publishCourse = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('publishCourse'))),
          switchMap((courseId) =>
            courses.publish(courseId).pipe(
              tapResponse({
                next: () => patchState(store, setPropLoaded('publishCourse')),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('publishCourse', error));
                },
              })
            )
          )
        )
      );

      const reportCourseItemProgress = rxMethod<{
        courseId: string;
        itemId: string;
        positionSeconds: number;
      }>(
        pipe(
          tap(() =>
            patchState(store, setPropLoading('reportCourseItemProgress'))
          ),
          switchMap(({ courseId, itemId, positionSeconds }) =>
            courses.itemProgress(courseId, itemId, positionSeconds).pipe(
              tapResponse({
                next: () =>
                  patchState(store, setPropLoaded('reportCourseItemProgress')),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(
                    store,
                    setPropError('reportCourseItemProgress', error)
                  );
                },
              })
            )
          )
        )
      );

      const addPathCourse = rxMethod<{
        pathId: string;
        courseId: string;
        order: number;
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('addPathCourse'))),
          switchMap(({ pathId, courseId, order }) =>
            paths.addCourse(pathId, courseId, order).pipe(
              tapResponse({
                next: () => patchState(store, setPropLoaded('addPathCourse')),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('addPathCourse', error));
                },
              })
            )
          )
        )
      );

      const publishPath = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('publishPath'))),
          switchMap((pathId) =>
            paths.publish(pathId).pipe(
              tapResponse({
                next: () => patchState(store, setPropLoaded('publishPath')),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('publishPath', error));
                },
              })
            )
          )
        )
      );

      /** Sequential course links, preserving order (addCourse is order-sensitive). */
      const addPathCourses$ = (pathId: string, courseIds: string[]) =>
        courseIds.length
          ? from(
              courseIds.map((courseId, order) => ({ courseId, order }))
            ).pipe(
              concatMap(({ courseId, order }) =>
                paths.addCourse(pathId, courseId, order)
              ),
              toArray()
            )
          : of([]);

      /**
       * Trilha assembly (`montar-trilha`): ONE emission performs the full chain —
       * `POST /LearningPaths` (draft) → optional `POST /{id}/cover` → sequential
       * `POST /{id}/courses` per course in list order → optional `POST /{id}/publish`.
       * Mirrors `assembleCourse`.
       */
      const assemblePath = rxMethod<{
        path: LearningPathCreateRequest;
        cover: File | null;
        courseIds: string[];
        publish: boolean;
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('assemblePath'))),
          switchMap(({ path, cover, courseIds, publish }) =>
            paths.create(path).pipe(
              switchMap((created) =>
                (cover
                  ? paths.setCover(created.LearningPathId, cover)
                  : of(null)
                ).pipe(
                  switchMap(() =>
                    addPathCourses$(created.LearningPathId, courseIds)
                  ),
                  switchMap(() =>
                    publish ? paths.publish(created.LearningPathId) : of(void 0)
                  ),
                  map(() =>
                    publish
                      ? { ...created, CourseStatusType: 'Published' as const }
                      : created
                  )
                )
              ),
              tapResponse({
                next: (created) =>
                  patchState(
                    store,
                    {
                      pathsById: index(
                        store.pathsById(),
                        [created],
                        (p) => p.LearningPathId
                      ),
                      producerPathIds: [
                        created.LearningPathId,
                        ...store
                          .producerPathIds()
                          .filter((id) => id !== created.LearningPathId),
                      ],
                    },
                    setPropLoaded('assemblePath')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('assemblePath', error));
                },
              })
            )
          )
        )
      );

      /**
       * `GET /OData/LearningPathCourses` — a path's ordered course links into
       * `pathCoursesByPath`. The endpoint is OData-config (not test-confirmed; see
       * `LearningPathsService.listCourses`), so a failure degrades to an EMPTY list WITHOUT a
       * global error alert: the edit/detail screens stay functional (append + publish still
       * work; the detail simply shows no course rows).
       */
      const loadPathCourses = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadPathCourses'))),
          switchMap((pathId) =>
            paths.getCourses(pathId).pipe(
              catchError(() => of([] as LearningPathCourseResponse[])),
              tap((value) =>
                patchState(
                  store,
                  {
                    pathCoursesByPath: {
                      ...store.pathCoursesByPath(),
                      [pathId]: value,
                    },
                  },
                  setPropLoaded('loadPathCourses')
                )
              )
            )
          )
        )
      );

      /**
       * Bulk-atomic reorder of a path's course links (immediate commit): optimistically reorder
       * `pathCoursesByPath[pathId]` by the given CourseId order, `PATCH /courses/reorder`, then
       * re-read the server truth (the backend stamps `Order = index`). On error, re-read reverts.
       */
      const reorderPathCourses = rxMethod<{
        pathId: string;
        orderedCourseIds: string[];
      }>(
        pipe(
          tap(({ pathId, orderedCourseIds }) => {
            const current = store.pathCoursesByPath()[pathId] ?? [];
            const byId = new Map(current.map((l) => [l.CourseId, l]));
            const reordered = orderedCourseIds
              .map((id) => byId.get(id))
              .filter((l): l is LearningPathCourseResponse => !!l);
            patchState(
              store,
              {
                pathCoursesByPath: {
                  ...store.pathCoursesByPath(),
                  [pathId]: reordered,
                },
              },
              setPropLoading('reorderPathCourses')
            );
          }),
          switchMap(({ pathId, orderedCourseIds }) =>
            paths.reorderCourses(pathId, orderedCourseIds).pipe(
              tapResponse({
                next: () => {
                  patchState(store, setPropLoaded('reorderPathCourses'));
                  loadPathCourses(pathId);
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('reorderPathCourses', error));
                  loadPathCourses(pathId); // revert to server state
                },
              })
            )
          )
        )
      );

      /** `DELETE /LearningPaths/{id}/courses/{courseId}` (immediate commit), then re-read. */
      const removePathCourse = rxMethod<{ pathId: string; courseId: string }>(
        pipe(
          tap(() => patchState(store, setPropLoading('removePathCourse'))),
          switchMap(({ pathId, courseId }) =>
            paths.removeCourse(pathId, courseId).pipe(
              tapResponse({
                next: () => {
                  patchState(store, setPropLoaded('removePathCourse'));
                  loadPathCourses(pathId);
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('removePathCourse', error));
                },
              })
            )
          )
        )
      );

      /** `DELETE /LearningPaths?id={guid}` (immediate commit), then re-read the producer list. */
      const removePath = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('removePath'))),
          switchMap((pathId) =>
            paths.remove(pathId).pipe(
              tapResponse({
                next: () => {
                  patchState(store, setPropLoaded('removePath'));
                  loadProducerPaths();
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('removePath', error));
                },
              })
            )
          )
        )
      );

      /**
       * Edit-mode append (`:pathId/edit`): the API has no path-course update/delete/reorder,
       * so editing is append-only — new courses are linked AFTER the existing ones
       * (`startOrder` = current course count) in list order, then the draft can be published.
       * Same one-emission composition rationale as `appendCourseItems`.
       */
      const appendPathCourses = rxMethod<{
        pathId: string;
        courseIds: string[];
        startOrder: number;
        publish: boolean;
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('appendPathCourses'))),
          switchMap(({ pathId, courseIds, startOrder, publish }) =>
            from(
              courseIds.map((courseId, offset) => ({
                courseId,
                order: startOrder + offset,
              }))
            ).pipe(
              concatMap(({ courseId, order }) =>
                paths.addCourse(pathId, courseId, order)
              ),
              toArray(),
              switchMap(() => (publish ? paths.publish(pathId) : of(void 0))),
              tapResponse({
                next: () => {
                  const cached = store.pathsById()[pathId];
                  patchState(
                    store,
                    publish && cached
                      ? {
                          pathsById: {
                            ...store.pathsById(),
                            [pathId]: {
                              ...cached,
                              CourseStatusType: 'Published',
                            },
                          },
                        }
                      : {},
                    setPropLoaded('appendPathCourses')
                  );
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('appendPathCourses', error));
                },
              })
            )
          )
        )
      );

      /** `POST /LearningPaths/{id}/cover` — replace a path cover (edit-mode, immediate). */
      const setPathCover = rxMethod<{ pathId: string; file: File }>(
        pipe(
          tap(() => patchState(store, setPropLoading('setPathCover'))),
          switchMap(({ pathId, file }) =>
            paths.setCover(pathId, file).pipe(
              tapResponse({
                next: () => patchState(store, setPropLoaded('setPathCover')),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('setPathCover', error));
                },
              })
            )
          )
        )
      );

      /** `GET /Courses/mine` — student-view summaries. Replaces the full list on each call. */
      const loadMyCourses = rxMethod<MineCoursesQuery | void>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadMyCourses'))),
          switchMap((query) =>
            courses.mine(query || {}).pipe(
              tapResponse({
                next: (res) =>
                  patchState(
                    store,
                    {
                      myCourses: res.items ?? [],
                      myCoursesTotal: res.total ?? 0,
                    },
                    setPropLoaded('loadMyCourses')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadMyCourses', error));
                },
              })
            )
          )
        )
      );

      /** `GET /LearningPaths/mine` — student-view summaries. Replaces the full list on each call. */
      const loadMyPaths = rxMethod<MinePathsQuery | void>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadMyPaths'))),
          switchMap((query) =>
            paths.mine(query || {}).pipe(
              tapResponse({
                next: (res) =>
                  patchState(
                    store,
                    { myPaths: res.items ?? [], myPathsTotal: res.total ?? 0 },
                    setPropLoaded('loadMyPaths')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadMyPaths', error));
                },
              })
            )
          )
        )
      );

      const reset = (): void =>
        patchState(store, initialAcademyLearningState, setPropInit(...ALL_OPS));

      /** Clear the upload op (progress + handshake + req-state) — call on form entry. */
      const resetUpload = (): void =>
        patchState(
          store,
          { uploadProgress: 0, lastUpload: null },
          setPropInit('uploadItemVideo')
        );

      /** Clear both assembly ops — call on builder entry (a stale Loaded would leave). */
      const resetAssemble = (): void =>
        patchState(store, setPropInit('assembleCourse', 'appendCourseItems'));

      /** Clear both path-assembly ops — call on builder entry (a stale Loaded would leave). */
      const resetAssemblePath = (): void =>
        patchState(store, setPropInit('assemblePath', 'appendPathCourses'));

      return {
        getCoursesDataSource,
        getPathsDataSource,
        discoverCourses,
        loadCourses,
        loadCourse,
        loadAllCourseItems,
        loadCourseItems,
        reorderCourseItems,
        removeCourseItem,
        updateCourseItem,
        updateCourse,
        removeCourse,
        setCourseItemRequired,
        uploadCourseItemVideo,
        resetUpload,
        assembleCourse,
        appendCourseItems,
        setCover,
        loadAttachments,
        addAttachment,
        removeAttachment,
        resetAssemble,
        discoverPaths,
        requestItemPlaybackToken,
        loadCourseProgress,
        loadPathProgress,
        createCourse,
        createPath,
        addCourseItem,
        publishCourse,
        reportCourseItemProgress,
        addPathCourse,
        publishPath,
        loadMyCourses,
        loadMyPaths,
        loadProducerPaths,
        loadPath,
        loadPathCourses,
        reorderPathCourses,
        removePathCourse,
        removePath,
        assemblePath,
        appendPathCourses,
        setPathCover,
        resetAssemblePath,
        reset,
      };
    }
  )
);
